import type { World } from '../sim/world';
import type { SimEvent } from '../sim/events';
import type { Vessel } from '../sim/vessel';
import { AMMO, type AmmoId } from '../data/classes';
import { SECTION_BOUNDS } from '../sim/damage';
import { hostile } from '../sim/captain';
import { MASTS } from '../data/ship';
import type { Sighting } from '../sim/lookout';
import type { Tracker } from './tracking';
import { agoText, bearingText, bearingTo, nmText, relBearing, KNOTS_PER_MS } from './nautical';
import { BOARDING_GAP, GRAPNEL_REACH, boardable, boardingWeight, canGrapple, hullGap, type BoardingFight } from '../sim/contact';
import type { BoardingPhase } from '../sim/events';
import type { Callouts } from './callouts';
import { PARTIES, type Party } from '../sim/crew';

// Everything about the fight: the state of our ship (guns, shot, hull
// sections, masts, water in the hold, fire, the people), who's out there,
// a running log of what just happened, and the end of it all.

const AMMO_NAMES: Record<AmmoId, string> = { round: 'Round shot', chain: 'Chain shot', grape: 'Grapeshot', shell: 'Explosive shell' };
const AMMO_HINT: Record<AmmoId, string> = { round: 'hull & guns', chain: 'sails & rigging', grape: 'crews · short range', shell: 'fire · explosive' };
const AMMO_SHORT: Record<AmmoId, string> = { round: 'Round', chain: 'Chain', grape: 'Grape', shell: 'Shell' };
const FACTION_NAMES: Record<string, string> = { navy: 'Navy', pirate: 'Pirate', merchant: 'Merchant', player: 'Ours' };
/** How a boarding stands, in words: for us boarding her, and for her people boarding us. */
const BOARD_WORDS: Record<BoardingPhase, [string, string]> = {
  hauling: ['Hauling her alongside…', 'Grapnels fast — they are hauling us alongside!'],
  rail: ['Driven back to the rail', 'Holding them at the rail'],
  foothold: ['A foothold on her deck', 'They have a foothold on our deck'],
  driving: ['Driving them aft', 'They are driving us aft!'],
  breaking: ['Their defence is breaking!', 'Our people are breaking!'],
  repelled: ['Thrown back', 'Thrown back'],
  carried: ['Her deck is carried', 'Our deck is carried'],
};

/** Odds in a boarding, in words, from the weight of each side (ours over theirs). */
function oddsWord(r: number): [string, string] {
  return r >= 1.7 ? ['good odds', 'good'] : r >= 1.15 ? ['fair odds', 'good'] : r >= 0.85 ? ['an even fight', ''] : r >= 0.55 ? ['poor odds', 'bad'] : ['hopeless odds', 'bad'];
}

/** Men in a ship's company as they'd look from alongside, rounded (her men below are a guess). */
function menSeen(v: Vessel): number {
  const n = v.crew.fighters + v.crew.reserve * 0.5;
  return n < 30 ? Math.round(n) : Math.round(n / 5) * 5;
}

const PARTY_NAMES: Record<Party, string> = { leaks: 'Leaks', fire: 'Fire', canvas: 'Canvas', spars: 'Spars' };
const PARTY_KEYS: Record<Party, string> = { leaks: 'U', fire: 'I', canvas: 'O', spars: 'P' };
const PARTY_HINT: Record<Party, string> = {
  leaks: 'Hands to the pumps and carpenters to the shot holes, ahead of the guns (U)',
  fire: 'Fire parties ahead of everything but the helm (I)',
  canvas: 'Sailmakers patch shot-through canvas, ahead of the guns (O)',
  spars: 'Riggers splice the rigging and send up spare spars for those shot away, ahead of the guns (P)',
};
const MAST_WORDS = ['', 'topgallant', 'topmast', 'jury mast'];

/** Her people's spirits in words. */
function spiritsWord(m: number): string {
  return m >= 0.8 ? 'in high spirits' : m >= 0.62 ? 'steady' : m >= 0.42 ? 'uneasy' : m >= 0.24 ? 'wavering' : 'shaken';
}

const MODE_NAMES: Record<string, string> = {
  patrol: 'On passage', investigate: 'Investigating', search: 'Searching', engage: 'Engaging', harry: 'Harrying', pursue: 'Giving chase',
  flee: 'Running', regroup: 'Falling back', board: 'Closing to board', ram: 'Bearing down to ram',
};

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, html?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

function healthColor(f: number): string {
  if (f > 0.75) return 'var(--ok)';
  if (f > 0.4) return '#d6b05a';
  if (f > 0.1) return 'var(--danger)';
  return '#6a2a20';
}

interface ContactRow {
  el: HTMLElement;
  name: HTMLElement;
  dist: HTMLElement;
  sub: HTMLElement;
  state: HTMLElement;
  bar: HTMLElement;
  barFill: HTMLElement;
}

interface LogLine {
  el: HTMLElement;
  t: number;
}

export class CombatHud {
  private readonly ship: HTMLElement;
  private readonly gunsP: HTMLElement;
  private readonly gunsS: HTMLElement;
  private readonly ammo: HTMLElement;
  private readonly hullSvg: SVGSVGElement;
  private readonly sectionEls: SVGPathElement[][] = [];
  private readonly mastEls: Record<string, SVGCircleElement> = {};
  private readonly rudderEl: SVGRectElement;
  private readonly status: HTMLElement;
  private readonly contacts: HTMLElement;
  private readonly log: HTMLElement;
  private readonly prompt: HTMLElement;
  /** The hull's soundness beside "Condition". */
  private readonly hullEl: HTMLElement;
  /** Under the broadside rows: the controls while a broadside is being laid (written by AimHud). */
  readonly aimHint: HTMLElement;
  private readonly end: HTMLElement;
  /** After the end: the bar that stays up while the player watches on. */
  private readonly bar: HTMLElement;
  /** Watching on after our ship is lost (the end card put away). */
  spectating = false;
  /** Where the big moments are said (set by the Hud). */
  callouts: Callouts | null = null;
  /** A damage party button pressed, and the grog (set by main: they go to the sim as commands). */
  onOrder: ((p: Party) => void) | null = null;
  onRally: (() => void) | null = null;
  private readonly dcBtns = {} as Record<Party, HTMLButtonElement>;
  private readonly grogBtn: HTMLButtonElement;
  private readonly dcNote: HTMLElement;
  /** Our people's morale, eased, to tell which way their spirits are going. */
  private spiritsSeen = -1;
  /** The last time a rake (ours, or of us) was said. */
  private readonly rakeSaid = new Map<string, number>();
  /** Our momentum a few seconds back, for "the men cheer". */
  private readonly feel: { t: number; m: number }[] = [];
  private readonly lines: LogLine[] = [];
  private readonly seen = new Set<number>();
  private readonly identified = new Set<number>();
  private readonly inSight = new Set<number>();
  private readonly contactRows = new Map<number, ContactRow>();
  private readonly contactsHead: HTMLElement;
  private endShown = false;
  private promptHtml = '';
  private timer = 0;
  private time = 0;

  constructor(hudWrap: HTMLElement, root: HTMLElement, private readonly world: World, private readonly tracker: Tracker, onRestart: () => void, private readonly onWatch: () => void) {
    // --- our ship (bottom right)
    this.ship = el('div', 'panel ship-panel');
    const head = el('div', 'sp-head');
    head.appendChild(el('div', 'stat-label', 'Broadsides'));
    this.ship.appendChild(head);
    this.gunsP = el('div', 'gunrow');
    this.gunsS = el('div', 'gunrow');
    this.aimHint = el('div', 'aim-hint hidden');
    this.ship.append(this.gunsP, this.gunsS, this.aimHint);
    this.ammo = el('div', 'ammo');
    this.ship.appendChild(this.ammo);
    const condHead = el('div', 'sp-head sp-sub');
    condHead.appendChild(el('div', 'stat-label', 'Condition'));
    this.hullEl = el('span', 'sp-hull');
    condHead.appendChild(this.hullEl);
    this.ship.appendChild(condHead);
    const cond = el('div', 'condition');
    this.hullSvg = this.buildHull();
    cond.appendChild(this.hullSvg);
    this.status = el('div', 'ship-status');
    cond.appendChild(this.status);
    this.ship.appendChild(cond);
    // Damage parties: men ordered off the guns to the leaks, fires, canvas, spars; and the grog.
    const dcHead = el('div', 'sp-head sp-sub');
    dcHead.appendChild(el('div', 'stat-label', 'Damage parties'));
    this.grogBtn = el('button', 'grog');
    this.grogBtn.type = 'button';
    this.grogBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onRally?.();
    });
    dcHead.appendChild(this.grogBtn);
    this.ship.appendChild(dcHead);
    const grid = el('div', 'dc-grid');
    for (const p of PARTIES) {
      const b = el('button', 'dc');
      b.type = 'button';
      b.title = PARTY_HINT[p];
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        this.onOrder?.(p);
      });
      grid.appendChild(b);
      this.dcBtns[p] = b;
    }
    this.ship.appendChild(grid);
    this.dcNote = el('div', 'dc-note');
    this.ship.appendChild(this.dcNote);
    hudWrap.appendChild(this.ship);
    this.rudderEl = this.hullSvg.querySelector('.rudder-seg') as SVGRectElement;

    // --- contacts (right)
    this.contacts = el('div', 'panel contacts hidden');
    this.contactsHead = el('div', 'stat-label', 'In sight');
    this.contacts.appendChild(this.contactsHead);
    hudWrap.appendChild(this.contacts);

    // --- log (top centre)
    this.log = el('div', 'log');
    hudWrap.appendChild(this.log);

    // --- boarding prompt (bottom centre)
    this.prompt = el('div', 'prompt hidden');
    hudWrap.appendChild(this.prompt);

    // --- end of the voyage
    this.end = el('div', 'endcard hidden');
    this.end.innerHTML = '<div class="end-inner"><div class="end-kicker"></div><h1></h1><p></p><div class="end-actions"><button class="end-go">Put to sea again</button><button class="end-watch">Continue watching</button></div></div>';
    this.end.querySelector('.end-go')!.addEventListener('click', onRestart);
    this.end.querySelector('.end-watch')!.addEventListener('click', () => this.watch());
    root.appendChild(this.end);
    this.bar = el('div', 'panel endbar hidden');
    this.bar.innerHTML = `<span class="endbar-title"></span><span class="endbar-hint">Free camera (2): WASD, R / F, mouse · wheel zoom · − / = speed · Y: the next ship · Esc frees the mouse</span><button class="end-go">Put to sea again</button>`;
    this.bar.querySelector('.end-go')!.addEventListener('click', onRestart);
    hudWrap.appendChild(this.bar);
  }

  private buildHull(): SVGSVGElement {
    // A plan view of the hull, bow to the right: five sections a side.
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '-20 -7 42 14');
    svg.classList.add('hull-plan');
    const X0 = -18.1, X1 = 18.9;
    const hb = (x: number) => {
      const u = (x - X0) / (X1 - X0);
      return 5.2 * Math.pow(Math.sin(Math.PI * Math.min(1, u * 0.92 + 0.06)), 0.55) * (u < 0.1 ? 0.75 + u * 2.5 : 1);
    };
    for (let s = 0; s < 5; s++) {
      const a = SECTION_BOUNDS[s]!, b = SECTION_BOUNDS[s + 1]!;
      const row: SVGPathElement[] = [];
      for (const side of [-1, 1]) {
        const pts: string[] = [];
        const n = 6;
        for (let i = 0; i <= n; i++) {
          const x = a + ((b - a) * i) / n;
          pts.push(`${x.toFixed(2)},${(side * hb(x)).toFixed(2)}`);
        }
        pts.push(`${b.toFixed(2)},0`, `${a.toFixed(2)},0`);
        const p = document.createElementNS(ns, 'path');
        // Port is up on the plan (looking down, bow to the right).
        p.setAttribute('d', `M${pts.join(' L')} Z`);
        p.classList.add('sec');
        svg.appendChild(p);
        row.push(p);
      }
      this.sectionEls.push(row);
    }
    for (const m of MASTS) {
      const c = document.createElementNS(ns, 'circle');
      c.setAttribute('cx', String(m.x));
      c.setAttribute('cy', '0');
      c.setAttribute('r', '1.1');
      c.classList.add('mast');
      svg.appendChild(c);
      this.mastEls[m.id] = c;
    }
    const r = document.createElementNS(ns, 'rect');
    r.setAttribute('x', '-19.6');
    r.setAttribute('y', '-0.5');
    r.setAttribute('width', '1.4');
    r.setAttribute('height', '1');
    r.classList.add('rudder-seg');
    svg.appendChild(r);
    return svg;
  }

  /** Messages from sim events. */
  handle(events: SimEvent[]): void {
    const w = this.world;
    const me = w.player;
    const name = (id: number) => w.vessels.find((v) => v.id === id)?.name ?? 'a ship';
    const big = this.callouts;
    // A ship of the fight: ours, or one near enough to matter.
    const near = (id: number) => {
      const v = w.vessels.find((o) => o.id === id);
      return !!v && (v === me || Math.hypot(v.ship.com.x - me.ship.com.x, v.ship.com.z - me.ship.com.z) < 3000);
    };
    for (const e of events) {
      switch (e.type) {
        case 'mastFall': {
          const v = e.vessel !== undefined ? w.vessels.find((o) => o.id === e.vessel)
            : w.vessels.find((o) => Math.hypot(o.ship.com.x - e.x, o.ship.com.z - e.z) < 30 * o.ship.sx + 10);
          const what = e.level === 3 ? `${e.mast}mast` : e.level === 2 ? `${e.mast} topmast` : `${e.mast} topgallant mast`;
          if (v === me) {
            this.say(`Our ${what} has gone by the board!`, 'bad');
            big?.show(`Our ${what} has gone by the board!`, e.level === 3 ? 'She will answer the helm slowly until the wreck is cut away' : '', 'bad', e.level === 3 ? 2 : 1);
          } else if (v) {
            this.say(`${v.name}'s ${what} is down.`, 'good');
            if (near(v.id)) big?.show(`Her ${what} is down!`, v.lastHitBy === me.id ? `${v.name} — our shot brought it down` : v.name, hostile(me, v) || hostile(v, me) ? 'good' : '', e.level === 3 ? 2 : 1);
          }
          break;
        }
        case 'sailTorn': {
          const sail = e.sail.toLowerCase();
          if (e.vessel === me.id) {
            if (e.level === 2) this.say(`Our ${sail} is in ribbons.`, 'bad');
          } else if (e.by === me.id) {
            this.say(`${name(e.vessel)}'s ${sail} ${e.level === 2 ? 'is in ribbons' : 'is shot through'}.`, 'good');
            if (e.level === 2) big?.show(`Her ${sail} is in ribbons`, name(e.vessel), 'good', 1);
          }
          break;
        }
        case 'fire':
          if (e.started && e.vessel === me.id) this.say('Fire! Fire aboard — hands to the buckets!', 'bad', 'fire');
          break;
        case 'explosion':
          if (e.size >= 4 && e.vessel >= 0) {
            this.say(e.vessel === me.id ? 'Our magazine has gone up!' : `${name(e.vessel)} blows up!`, e.vessel === me.id ? 'bad' : 'good');
            if (near(e.vessel)) big?.show(e.vessel === me.id ? 'Our magazine has gone up!' : `${name(e.vessel)} blows up!`, '', e.vessel === me.id ? 'bad' : 'good', 2);
          }
          break;
        case 'struck': {
          this.say(`${name(e.vessel)} strikes her colours.`, 'good');
          const v = w.vessels.find((o) => o.id === e.vessel);
          if (v && near(v.id)) {
            const ours = hostile(me, v) || hostile(v, me);
            big?.show(`${v.name} strikes her colours!`, ours ? 'Victory — close and board her to take possession (B)' : '', ours ? 'good' : '', 2);
          }
          break;
        }
        case 'captured':
          if (e.vessel === me.id) this.say('They have carried our deck.', 'bad');
          else if (e.by === me.id) {
            this.say(`${name(e.vessel)} is ours! Her colours come down and a prize crew goes aboard.`, 'good');
            big?.show(`${name(e.vessel)} is ours!`, 'A prize crew goes aboard', 'good', 2);
          } else this.say(`${name(e.vessel)} is taken by ${name(e.by)}.`, '');
          break;
        case 'shipSinking':
          if (e.vessel === me.id) this.say('She is going down! Abandon ship!', 'bad');
          else {
            this.say(`${name(e.vessel)} is sinking.`, 'good');
            if (near(e.vessel)) big?.show(`${name(e.vessel)} is going down!`, '', 'good', 2);
          }
          break;
        case 'teleport':
          // (A move of nothing is the dev tools turning her on the spot.)
          if (e.vessel === me.id && Math.hypot(e.dx, e.dz) > 1) this.say(`Dev: moved ${nmText(Math.hypot(e.dx, e.dz))} ${bearingText(Math.atan2(e.dx, -e.dz)).split(' ')[0]}.`, '');
          break;
        case 'shipSunk':
          if (e.vessel !== me.id) this.say(`${name(e.vessel)} has gone to the bottom.`, '');
          break;
        case 'boarding':
          if (e.a === me.id || e.b === me.id) {
            const f = w.contacts.fightOf(me.id);
            const theyBoard = f ? f.attacker !== me.id : false;
            const other = name(e.a === me.id ? e.b : e.a);
            this.say(theyBoard ? `${other} throws her grapnels across — boarders!` : `Grapnels across — boarding ${other}!`, theyBoard ? 'bad' : '');
            if (theyBoard) big?.show('Boarders!', `${other} is coming aboard — B: all hands to repel them`, 'bad', 2);
          }
          break;
        case 'boardingTurn': {
          if (e.attacker !== me.id && e.defender !== me.id) break;
          const ours = e.attacker === me.id;
          const other = name(ours ? e.defender : e.attacker);
          if (e.phase === 'repelled') {
            this.say(ours ? `Our boarders are thrown back from ${other}.` : `We have thrown ${other}'s boarders back!`, ours ? 'bad' : 'good');
            big?.show(ours ? 'Our boarders are thrown back' : 'Boarders repelled!', other, ours ? 'bad' : 'good', 2);
          } else if (e.phase !== 'carried' && e.phase !== 'hauling') {
            const [mine, theirs] = BOARD_WORDS[e.phase];
            const good = ours ? e.phase === 'driving' || e.phase === 'breaking' : e.phase === 'rail';
            const bad = ours ? e.phase === 'rail' : e.phase === 'driving' || e.phase === 'breaking';
            this.say(ours ? mine : theirs, good ? 'good' : bad ? 'bad' : '', 'boardTurn');
          }
          break;
        }
        case 'raked': {
          // Said once for a raking broadside (each ball raises one).
          if (e.by !== me.id && e.vessel !== me.id) break;
          const key = `${e.vessel}:${e.by}`;
          if (this.time - (this.rakeSaid.get(key) ?? -Infinity) < 4) break;
          this.rakeSaid.set(key, this.time);
          if (e.vessel === me.id) {
            this.say(`${name(e.by)} rakes us from ${e.end === 'stern' ? 'astern' : 'ahead'}!`, 'bad');
            big?.show(`Raked from ${e.end === 'stern' ? 'astern' : 'ahead'}!`, `${name(e.by)}'s shot is coming the length of our deck`, 'bad', 2);
          } else {
            this.say(`We rake ${name(e.vessel)} through her ${e.end}!`, 'good');
            big?.show(`Raked her ${e.end}!`, `${name(e.vessel)} — our shot runs the length of her deck`, 'good', 2);
          }
          break;
        }
        case 'sparRigged':
          if (e.vessel === me.id) {
            const what = `${e.mast} ${MAST_WORDS[e.level]}`;
            this.say(e.level === 3 ? `A jury ${e.mast}mast is stepped and rigged.` : `A spare ${what} is sent up and her canvas bent on.`, 'good');
            big?.show(e.level === 3 ? `Jury ${e.mast}mast stepped` : `Spare ${what} sent up`, 'Her canvas is bent on new — make sail when ready', 'good', 1);
          }
          break;
        case 'sailMended':
          if (e.vessel === me.id) this.say(`The ${e.sail.toLowerCase()} is patched and drawing again.`, 'good', `mend${e.sail}`);
          break;
        case 'rally':
          if (e.vessel === me.id) {
            this.say(`Grog served out — the men give a cheer! (${e.left} cask${e.left === 1 ? '' : 's'} left)`, 'good');
            big?.show('Grog all round!', 'Heart in the men — but the gun captains’ hands are less steady for a while', 'good', 1);
          } else if (near(e.vessel)) {
            this.say(`A cheer from ${name(e.vessel)}'s deck — they're serving out grog.`, '', `grog${e.vessel}`);
          }
          break;
        case 'cutRefused':
          if (e.vessel === me.id) this.say('They hold too much of our deck for the lines to be cut!', 'bad', 'cut');
          break;
        case 'collision':
          if (Math.hypot(e.x - me.ship.com.x, e.z - me.ship.com.z) < 60) this.say('Collision!', 'bad', 'collision');
          break;
        case 'collapse':
          if (e.what === 'keel' && Math.hypot(e.x - me.ship.com.x, e.z - me.ship.com.z) < 40) this.say('Her back is broken!', 'bad');
          break;
        default:
          break;
      }
    }
  }

  /** A line in the log from elsewhere in the HUD (`dedupe`: not again within a few seconds). */
  note(text: string, kind: '' | 'good' | 'bad', dedupe?: string): void {
    this.say(text, kind, dedupe);
  }

  private say(text: string, kind: '' | 'good' | 'bad', dedupe?: string): void {
    if (dedupe && this.lines.some((l) => l.el.dataset.key === dedupe && this.time - l.t < 8)) return;
    const d = el('div', `log-line ${kind}`, text);
    if (dedupe) d.dataset.key = dedupe;
    this.log.prepend(d);
    this.lines.unshift({ el: d, t: this.time });
    while (this.lines.length > 6) this.lines.pop()!.el.remove();
  }

  /** Put the end card away and watch on: the sea, the other ships, the wreck. */
  private watch(): void {
    this.end.classList.add('hidden');
    this.bar.querySelector('.endbar-title')!.textContent = this.end.querySelector('h1')!.textContent;
    this.bar.classList.remove('hidden');
    this.spectating = true;
    this.onWatch();
  }

  update(dt: number): void {
    this.time += dt;
    // Back in the fight after all (the dev tools can repair a taken ship): no more watching.
    if (this.spectating && this.world.player.state === 'active') {
      this.spectating = false;
      this.endShown = false;
      this.bar.classList.add('hidden');
    }
    for (const l of this.lines) {
      const age = this.time - l.t;
      l.el.style.opacity = String(Math.max(0, Math.min(1, (10 - age) / 2)));
    }
    while (this.lines.length && this.time - this.lines[this.lines.length - 1]!.t > 10) this.lines.pop()!.el.remove();
    const w = this.world;
    const me = w.player;
    this.renderGuns(this.gunsP, -1, 'Port', 'Q');
    this.renderGuns(this.gunsS, 1, 'Starboard', 'E');
    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer = 0.2;

    // Shot.
    const cur = me.battery.ammo;
    const loaded = new Map<AmmoId, number>();
    for (const g of me.battery.guns) if (g.loaded && !g.destroyed) loaded.set(g.charge, (loaded.get(g.charge) ?? 0) + 1);
    const loadedText = [...loaded.entries()].map(([a, n]) => `${n} ${AMMO[a].id}`).join(' · ') || 'none loaded';
    const ammoHtml = `<div class="ammo-head"><span class="gun-key">G</span><span class="stat-label">Shot</span><span class="ammo-loaded">${loadedText}</span></div>`
      + `<div class="ammo-seg">${me.cls.ammo.map((a) => `<span class="${a === cur ? 'on' : ''}" title="${AMMO_NAMES[a]}">${AMMO_SHORT[a]}</span>`).join('')}</div>`
      + `<div class="ammo-hint">${AMMO_NAMES[cur]} · ${AMMO_HINT[cur]}</div>`;
    if (this.ammo.innerHTML !== ammoHtml) this.ammo.innerHTML = ammoHtml;

    // Hull plan.
    const d = me.damage;
    this.sectionEls.forEach((row, s) => {
      row.forEach((p, i) => {
        const f = Math.max(0, d.hull[s]![i === 0 ? 0 : 1]! / d.hullMax);
        p.style.fill = healthColor(f);
        p.style.fillOpacity = String(0.35 + 0.5 * (1 - f));
      });
    });
    for (const m of MASTS) {
      const c = this.mastEls[m.id]!;
      const md = d.masts[m.id];
      c.style.display = md ? '' : 'none';
      if (!md) continue;
      c.style.fill = md.lostLevel >= 3 ? 'transparent' : healthColor(md.hp / md.max);
      c.style.stroke = md.lostLevel > 0 ? 'var(--danger)' : 'var(--ink)';
      c.setAttribute('stroke-dasharray', md.lostLevel > 0 ? '0.6 0.4' : '');
    }
    this.rudderEl.style.fill = healthColor(d.rudder);

    // Condition in words.
    const s = me.ship;
    const crew = me.crew;
    const fit = crew.effective;
    const water = s.flooding;
    const leak = d.leakArea;
    const fires = me.fire.burning;
    const pct = (f: number) => Math.round(Math.max(0, Math.min(1, f)) * 100);
    const tile = (label: string, value: string, sub: string, frac: number, color: string, warn = false) =>
      `<div class="ct${warn ? ' warn' : ''}"><div class="ct-top"><span>${label}</span><b>${value}</b></div>`
      + `<div class="ct-bar"><i style="width:${pct(frac)}%;background:${color}"></i></div><em>${sub}</em></div>`;
    const masts = Object.values(d.masts);
    const standing = masts.filter((m) => m.lostLevel < 3).length;
    const mastHealth = masts.length ? masts.reduce((a, m) => a + (m.lostLevel >= 3 ? 0 : (m.hp / m.max) * (1 - m.lostLevel / 3)), 0) / masts.length : 1;
    const rig = s.sails.sailHealth;
    const tiles: string[] = [];
    // Which way their spirits are going.
    if (this.spiritsSeen < 0) this.spiritsSeen = crew.morale;
    this.spiritsSeen += (crew.morale - this.spiritsSeen) * 0.04;
    const trend = crew.morale - this.spiritsSeen;
    const arrow = trend > 0.012 ? ' <span class="trend up">▲</span>' : trend < -0.012 ? ' <span class="trend down">▼</span>' : '';
    tiles.push(tile('Crew', `${fit}<small>/${crew.complement}</small>`, `${spiritsWord(crew.morale)}${me.grog > 0.05 ? ' · grog' : ''}${arrow}`, crew.morale, healthColor(crew.morale), crew.morale < 0.3));
    this.feel.push({ t: this.time, m: crew.momentum });
    while (this.feel.length && this.time - this.feel[0]!.t > 4) this.feel.shift();
    const lo = Math.min(...this.feel.map((f) => f.m)), hi = Math.max(...this.feel.map((f) => f.m));
    if (crew.momentum - lo > 0.45) this.say('A cheer goes up from our guns!', 'good', 'cheer');
    else if (hi - crew.momentum > 0.45) this.say('That broadside has shaken our people.', 'bad', 'shaken');
    tiles.push(tile('Hold', `${pct(water)}%`, leak > 0.01 ? (crew.pumpers > 0.5 ? 'leaking · pumps manned' : 'leaking') : crew.pumpers > 0.5 ? 'pumping' : 'dry',
      Math.max(water > 0 ? 0.03 : 0, water), water > 0.3 ? 'var(--danger)' : 'linear-gradient(90deg, #4f8fb0, #7fb6d1)', water > 0.3));
    tiles.push(tile('Rigging', `${pct(rig)}%`, rig > 0.9 ? 'all drawing' : rig > 0.5 ? 'shot through' : 'in tatters', rig, healthColor(rig), rig < 0.4));
    tiles.push(tile('Masts', `${standing}<small>/${masts.length}</small>`, d.rudder > 0.95 ? 'rudder sound' : d.rudder > 0.05 ? `rudder ${pct(d.rudder)}%` : 'rudder shot away', mastHealth, healthColor(mastHealth), standing < masts.length || d.rudder < 0.3));
    const open = d.holes.filter((h) => h.patched < 0.95).length, plugged = d.holes.length - open;
    const alerts: string[] = [];
    if (fires > 0) alerts.push(`<span class="chip bad">Fire ×${fires} · ${crew.fighters > 0 ? 'fighting it' : 'unchecked!'}</span>`);
    if (open > 0) alerts.push(`<span class="chip warn">${open} shot hole${open > 1 ? 's' : ''}${plugged ? ` · ${plugged} plugged` : ''}</span>`);
    else if (plugged > 0) alerts.push(`<span class="chip">${plugged} hole${plugged > 1 ? 's' : ''} plugged</span>`);
    const rows = [`<div class="cond-grid">${tiles.join('')}</div>`];
    if (alerts.length) rows.push(`<div class="cond-alerts">${alerts.join('')}</div>`);
    const sound = Math.round(Math.max(0, d.integrity) * 100);
    this.hullEl.textContent = `Hull ${sound}%`;
    this.hullEl.className = `sp-hull${sound < 50 ? ' warn' : ''}`;
    this.status.innerHTML = rows.join('');
    this.renderParties();

    this.renderContacts();
    this.renderPrompt();
    this.renderEnd();
  }

  /** The damage parties: who's ordered to it, how many men, and how the work stands. */
  private renderParties(): void {
    const me = this.world.player;
    const d = me.damage, s = me.ship, crew = me.crew;
    const pct = (f: number) => Math.round(Math.max(0, Math.min(1, f)) * 100);
    const open = d.holes.filter((h) => h.patched < 1).length;
    const torn = s.sails.sails.filter((x) => !x.lost && x.cloth.health < 0.97);
    const canvas = torn.length ? torn.reduce((a, x) => a + x.cloth.health, 0) / torn.length : 1;
    const lost = Object.values(d.masts).filter((m) => m.lostLevel > 0);
    const raising = lost.find((m) => m.wreck === 0 && m.jury > 0) ?? lost.find((m) => m.wreck === 0);
    const rig = Object.values(d.masts).filter((m) => m.lostLevel < 3);
    const rigging = rig.length ? Math.min(...rig.map((m) => m.rigging)) : 1;
    const work: Record<Party, { text: string; frac: number; need: boolean }> = {
      leaks: {
        text: s.flooding > 0.01 || open ? `${pct(s.flooding)}% · ${open} hole${open === 1 ? '' : 's'}` : 'tight',
        frac: open || s.flooding > 0.01 ? 1 - s.flooding : 1, need: open > 0 || s.flooding > 0.05,
      },
      fire: { text: me.fire.burning ? `${me.fire.burning} burning` : 'none', frac: 1 - me.fire.worst, need: me.fire.burning > 0 },
      canvas: { text: torn.length ? `${torn.length} torn · ${pct(canvas)}%` : 'all whole', frac: canvas, need: torn.length > 0 },
      spars: {
        text: raising && d.spares > 0 ? `${MAST_WORDS[raising.lostLevel]} ${pct(raising.jury)}%`
          : lost.length ? (d.spares > 0 ? 'clearing wreck' : 'no spares left') : rigging < 0.85 ? `rigging ${pct(rigging)}%` : 'all standing',
        frac: raising ? raising.jury : rigging, need: lost.length > 0 || rigging < 0.85,
      },
    };
    for (const p of PARTIES) {
      const b = this.dcBtns[p];
      const wk = work[p];
      const men = crew.party[p];
      const html = `<span class="gun-key">${PARTY_KEYS[p]}</span><b>${PARTY_NAMES[p]}</b><small>${men ? `${men} men` : '—'}</small>`
        + `<em>${wk.text}</em><i style="width:${pct(wk.frac)}%"></i>`;
      if (b.innerHTML !== html) b.innerHTML = html;
      b.classList.toggle('on', me.orders[p]);
      b.classList.toggle('need', wk.need && !me.orders[p]);
    }
    const grog = `<span class="gun-key">J</span>Grog <small>×${me.grogCasks}</small>`;
    if (this.grogBtn.innerHTML !== grog) this.grogBtn.innerHTML = grog;
    this.grogBtn.disabled = me.grogCasks <= 0 || me.grog > 0.2;
    this.grogBtn.title = me.grogCasks <= 0 ? 'The grog is all gone' : me.grog > 0.2 ? 'They have had their grog' : 'Serve out grog: heart for wavering men, but less steady gun captains for a while (J)';
    const ordered = PARTIES.filter((p) => me.orders[p]);
    const off = ordered.reduce((a, p) => a + crew.party[p], 0);
    const guns = me.battle ? `Guns manned by ${crew.party.guns}` : 'Not at quarters';
    const note = ordered.length ? `${guns} · ${off} men on orders` : `${guns} · the officers see to the rest`;
    if (this.dcNote.textContent !== note) this.dcNote.textContent = note;
  }

  private renderGuns(row: HTMLElement, side: 1 | -1, name: string, key: string): void {
    const guns = this.world.player.battery.guns.filter((g) => g.spec.side === side);
    const pips = guns.map((g) => {
      if (g.destroyed) return '<i class="gone"></i>';
      const cls = g.loaded ? (g.crew > 0.2 ? 'ready' : 'loaded') : g.crew > 0.2 ? '' : 'idle';
      return `<i class="${cls}" style="--p:${(g.loaded ? 1 : g.reload).toFixed(2)}"></i>`;
    }).join('');
    const live = guns.filter((g) => !g.destroyed);
    const ready = live.filter((g) => g.loaded).length;
    const state = !live.length ? '<span class="gun-state bad">lost</span>'
      : ready === live.length ? '<span class="gun-state ok">Ready</span>'
      : ready > 0 ? `<span class="gun-state">${ready}/${live.length}</span>`
      : `<span class="gun-state">${Math.round(this.world.player.battery.reloadProgress(side) * 100)}%</span>`;
    const html = `<span class="gun-key">${key}</span><span class="gun-name">${name}</span><span class="pips">${pips}</span>${state}`;
    if (row.innerHTML !== html) row.innerHTML = html;
    row.classList.toggle('aiming', this.world.player.battery.aim?.side === side);
  }

  private renderContacts(): void {
    const w = this.world;
    const me = w.player;
    const p = me.ship.com;
    // Only what our own lookouts have actually seen.
    const known = w.lookout.known();
    const items: { s: Sighting; v: Vessel | undefined; d: number; brg: number }[] = [];
    for (const s of known) {
      const v = w.vessels.find((o) => o.id === s.vessel);
      const d = Math.hypot(s.x - p.x, s.z - p.z);
      const brg = bearingTo(p.x, p.z, s.x, s.z);
      items.push({ s, v, d, brg });
      if (!this.seen.has(s.vessel)) {
        this.seen.add(s.vessel);
        const what = s.identified && v ? `A ${v.cls.name.toLowerCase()}` : 'A sail';
        this.say(`Sail ho! ${what} ${relBearing(brg - me.ship.heading)}, bearing ${bearingText(brg)}.`, '');
      }
      if (s.identified && v && !this.identified.has(v.id)) {
        this.identified.add(v.id);
        if (hostile(v, me) || hostile(me, v)) this.say(`She's ${v.name} — ${FACTION_NAMES[v.faction]}${v.faction === 'navy' ? ', and she means us harm' : ''}.`, v.faction === 'merchant' ? '' : 'bad');
      }
      const was = this.inSight.has(s.vessel);
      if (s.visible && !was) this.inSight.add(s.vessel);
      if (!s.visible && was) {
        this.inSight.delete(s.vessel);
        this.say(`Lost sight of ${s.identified && v ? v.name : 'the strange sail'}, last seen ${bearingText(brg)}.`, '', `lost${s.vessel}`);
      }
    }
    // In sight first, nearest first.
    items.sort((a, b) => Number(b.s.visible) - Number(a.s.visible) || a.d - b.d);
    const shown = items.slice(0, 6);
    this.contacts.classList.toggle('hidden', shown.length === 0);
    const keep = new Set(shown.map((x) => x.s.vessel));
    for (const [id, row] of this.contactRows) {
      if (keep.has(id)) continue;
      row.el.remove();
      this.contactRows.delete(id);
    }
    let prev: HTMLElement = this.contactsHead;
    for (const { s, v, d, brg } of shown) {
      let row = this.contactRows.get(s.vessel);
      if (!row) {
        row = this.makeRow(s.vessel);
        this.contactRows.set(s.vessel, row);
      }
      if (prev.nextSibling !== row.el) prev.after(row.el);
      prev = row.el;
      const known = s.identified && v;
      row.el.className = `contact ${known ? v.faction : 'unknown'}${s.visible ? '' : ' lost'}${this.tracker.selected === s.vessel ? ' tracked' : ''}`;
      row.name.textContent = known ? v.name : 'Strange sail';
      row.dist.textContent = `${s.visible ? '' : '≈ '}${nmText(d)}`;
      row.sub.textContent = `${known ? `${v.cls.name} · ` : ''}${bearingText(brg)}, ${relBearing(brg - me.ship.heading)}`;
      let state = '';
      if (!s.visible) state = `Lost to sight ${agoText(w.time - s.seen)}`;
      else if (known && d < 4000) {
        state = v.state === 'struck' ? 'Colours struck' : v.state === 'sinking' ? 'Sinking' : v.state === 'captured' ? 'Taken' : MODE_NAMES[v.captain?.mode ?? 'patrol'] ?? '';
      }
      row.state.textContent = state;
      // Her damage can only be made out close to.
      const hull = known && s.visible && d < 2000 ? v.damage.integrity : -1;
      row.bar.style.display = hull < 0 ? 'none' : '';
      if (hull >= 0) {
        row.barFill.style.width = `${Math.round(hull * 100)}%`;
        row.barFill.style.background = healthColor(hull);
      }
    }
  }

  private makeRow(id: number): ContactRow {
    const el = document.createElement('div');
    el.title = 'Keep a glass on her';
    const top = document.createElement('div');
    top.className = 'c-top';
    const flag = document.createElement('i');
    flag.className = 'flag';
    const name = document.createElement('b');
    const dist = document.createElement('span');
    top.append(flag, name, dist);
    const sub = document.createElement('div');
    sub.className = 'c-sub';
    const state = document.createElement('div');
    state.className = 'c-sub';
    const bar = document.createElement('div');
    bar.className = 'bar';
    const barFill = document.createElement('i');
    bar.appendChild(barFill);
    el.append(top, sub, state, bar);
    el.addEventListener('click', () => this.tracker.toggle(id));
    return { el, name, dist, sub, state, bar, barFill };
  }

  /** The nearest ship we might board, and whether we can now (same rule as the sim). */
  private boardingTarget(): { v: Vessel; check: ReturnType<typeof canGrapple> } | null {
    const w = this.world;
    const me = w.player;
    let best: { v: Vessel; check: ReturnType<typeof canGrapple> } | null = null;
    for (const v of w.vessels) {
      if (v === me || !boardable(v) || !hostile(me, v)) continue;
      const check = canGrapple(me, v);
      if (check.gap > GRAPNEL_REACH + 60) continue;
      if (!best || check.gap < best.check.gap) best = { v, check };
    }
    return best;
  }

  private renderPrompt(): void {
    const w = this.world;
    const me = w.player;
    let show = '';
    const fight = me.boardingWith >= 0 ? w.contacts.fightOf(me.id) : null;
    this.prompt.classList.toggle('boarding', !!fight);
    if (me.boardingWith >= 0) {
      const b = w.vessels.find((v) => v.id === me.boardingWith);
      if (b && fight) show = this.boardingPanel(fight, b);
      else if (b) {
        show = hullGap(me, b) > BOARDING_GAP
          ? `Grapnels fast — hauling <b>${b.name}</b> alongside…`
          : `Boarding <b>${b.name}</b>`;
      }
    } else if (me.fighting) {
      const t = this.boardingTarget();
      if (t) {
        const { v, check } = t;
        const what = v.state === 'struck' ? 'Take possession of' : 'Board';
        // What we'd be taking on: her people as they look from here.
        const [odds, tone] = oddsWord(boardingWeight(me) / Math.max(0.1, boardingWeight(v)));
        const sub = v.state === 'struck' ? '' : `<span class="odds ${tone}">our ${menSeen(me)} against about ${menSeen(v)} — ${odds}</span>`;
        if (check.ok) show = `<span class="gun-key">B</span> ${what} ${v.name} ${sub}`;
        else if (check.why === 'fast') show = `Alongside ${v.name} — match her speed to board (${(check.rel * KNOTS_PER_MS).toFixed(1)} kn between us)`;
        else if (check.why === 'far') show = `${v.name} ${Math.round(check.gap)} m off — close within ${GRAPNEL_REACH} m to throw grapnels`;
      }
    }
    this.prompt.classList.toggle('hidden', !show);
    // (Compared with what was written, not read back: the bar eases between widths.)
    if (show && this.promptHtml !== show) this.prompt.innerHTML = this.promptHtml = show;
  }

  /**
   * The fight for the deck, as we see it: who's aboard whom, a bar for how
   * much of the deck in question our people hold, how it's going in words,
   * the men lost each side, and the two orders - all hands, or cut the lines.
   */
  private boardingPanel(f: BoardingFight, b: Vessel): string {
    const me = this.world.player;
    const ours = f.attacker === me.id;
    const held = ours ? f.hold : 1 - f.hold;
    const words = BOARD_WORDS[f.phase][ours ? 0 : 1];
    const gaining = (ours ? f.swing : -f.swing);
    const tone = f.phase === 'hauling' ? '' : held > 0.62 ? 'good' : held < 0.35 ? 'bad' : '';
    const trend = f.phase === 'hauling' ? '' : gaining > 0.3 ? '<span class="trend up">gaining</span>' : gaining < -0.3 ? '<span class="trend down">losing ground</span>' : '';
    const usW = ours ? f.weightA : f.weightD, themW = ours ? f.weightD : f.weightA;
    const lostUs = ours ? f.lostA : f.lostD, lostThem = ours ? f.lostD : f.lostA;
    const head = ours ? `Boarding <b>${b.name}</b>` : `<b>${b.name}</b> is boarding us`;
    const bar = f.phase === 'hauling' ? '' : `<div class="bp-bar"><i style="width:${Math.round(held * 100)}%"></i><span class="bp-mid"></span></div>`
      + `<div class="bp-ends"><span>${ours ? 'Her rail' : 'Lost'}</span><span>${ours ? 'Her deck carried' : 'Our deck held'}</span></div>`;
    const men = f.phase === 'hauling' ? '' : `<div class="bp-men"><span>In the fight: ours ~${Math.round(usW)} · theirs ~${Math.round(themW)}</span><span>Fallen: ours ${lostUs} · theirs ${lostThem}</span></div>`;
    const keys = `<div class="bp-keys"><span class="${me.allHands ? 'on' : ''}"><kbd>B</kbd> ${me.allHands ? 'All hands are in it — back to the guns' : (ours ? 'All hands to the boarding' : 'All hands to repel boarders')}</span>`
      + `<span><kbd>K</kbd> ${ours ? 'Cut the lines and fall back' : 'Cut the grapnels'}</span></div>`;
    return `<div class="bp-head">${head}</div>${bar}<div class="bp-words ${tone}">${words} ${trend}</div>${men}${keys}`;
  }

  /** B pressed: if no grapnels will go across, say why. */
  boardOrder(): void {
    const me = this.world.player;
    if (me.boardingWith >= 0) return;
    if (!me.fighting) return this.say('We are in no state to board anyone.', 'bad', 'board');
    const t = this.boardingTarget();
    if (!t) return this.say('Nobody near enough to board.', '', 'board');
    const { v, check } = t;
    if (check.why === 'far') this.say(`Too far for grapnels: close within ${GRAPNEL_REACH} m of ${v.name}'s side (${Math.round(check.gap)} m now).`, '', 'board');
    else if (check.why === 'fast') this.say(`Too fast for grapnels: match ${v.name}'s speed (${(check.rel * KNOTS_PER_MS).toFixed(1)} kn between us).`, '', 'board');
  }

  private renderEnd(): void {
    const me = this.world.player;
    if (this.endShown) return;
    let kicker = '', title = '', text = '';
    if (me.state === 'sunk' || (me.state === 'sinking' && me.sinkTime > 25)) {
      kicker = 'Lost with all hands';
      title = `${me.name} has foundered`;
      text = `${me.crew.killed} of her people killed. The sea keeps the rest of the story.`;
    } else if (me.state === 'captured') {
      kicker = 'Taken';
      title = `${me.name} has been carried by boarders`;
      text = 'Her colours come down, and her company go below in irons.';
    }
    if (!title) return;
    this.endShown = true;
    this.end.querySelector('.end-kicker')!.textContent = kicker;
    this.end.querySelector('h1')!.textContent = title;
    this.end.querySelector('p')!.textContent = text;
    setTimeout(() => this.end.classList.remove('hidden'), 3500);
  }
}
